import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { BiX } from 'react-icons/bi';
import { ValidationError } from 'yup';
import { FormHandles } from '@unform/core';
import { IoIosWarning } from 'react-icons/io';

import Tab from '../../../../newComponents/Tab';
import TabBar from '../../../../newComponents/TabBar';
import Button from '../../../../newComponents/Button';
import { ModalHandles } from '../../../../newComponents/Modal';

import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import { Image, useGridProduct } from '../../../../hooks/gridProduct';

import Details, { DetailsFormSchema } from './steps/Details';
import Grids, { GridsFormSchema } from './steps/Grids';
import Complements, { ComplementsFormSchema } from './steps/Complements';
import Availability, { AvailabilityFormSchema } from './steps/Availability';

import getValidationErrors from '../../../../utils/getValidationErrors';

import ColorsService from '../../../../services/ColorsService';
import ProductsService from '../../../../services/ProductsService';
import { Product } from '../../../../services/GroupsService';

import APIError from '../../../../errors/APIError';

import { Container, TabPanel, Modal, ModalHeader } from './styles';
import SizesService from '../../../../services/SizesService';
import Dimensions, { DimensionsFormSchema } from './steps/Dimensions';
import empresaApi from '../../../../services/empresaApi';

type GridProductProps = {
  lastProductIndex: number;
  onSuccessSave(groupId: number, product: Product): void;
};

const LAST_TAB_INDEX = 3;

const SCHEMAS = {
  0: DetailsFormSchema,
  1: GridsFormSchema,
  2: ComplementsFormSchema,
  3: AvailabilityFormSchema,
  4: DimensionsFormSchema,
};

const GridProduct: React.FC<GridProductProps> = ({
  lastProductIndex,
  onSuccessSave,
}) => {
  const isFirstRender = useRef(true);
  const formRef = useRef<FormHandles>(null);
  const modalRef = useRef<ModalHandles>(null);
  const formMainRef = useRef<HTMLDivElement>(null);

  const {
    id,
    name,
    gtin,
    observation,
    grids,
    image,
    details,
    groupId,
    currentTab,
    complements,
    groupsOptions,
    isDrawerOpened,
    unitOfMeasurementId,
    isAwaysAvailability,
    unitsOfMeasurementOptions,
    isComplementsDrawerOpened,
    handleChangeTab,
    handleCloseDrawer,
    handleResetProduct,
    handleChangeSizesOptions,
    handleChangeColorsOptions,
    width,
    height,
    length,
    weight,
  } = useGridProduct();
  const { addToast } = useToast();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [bestShippingIsActive, setBestShippingIsActive] = useState(false);

  const handleCancel = useCallback(() => {
    modalRef.current?.close();
    handleCloseDrawer();

    setTimeout(() => {
      handleChangeTab(0);
      handleResetProduct();
      formRef.current?.reset();
      formRef.current?.setErrors({});
    }, 500);
  }, [handleCloseDrawer, handleChangeTab, handleResetProduct]);

  const handleTryCancel = useCallback(() => {
    modalRef.current?.open();
  }, []);

  const handleLoadSizes = useCallback(async () => {
    try {
      const sizesList = await SizesService.listSizes(user.empresa_id, 2);

      handleChangeSizesOptions(
        sizesList.map((size) => ({
          value: size.id,
          label: size.name,
        })),
      );
    } catch (error) {
      if (error instanceof APIError) {
        addToast({
          title: error.message,
          type: 'error',
        });
      }
    }
  }, [user.empresa_id, addToast, handleChangeSizesOptions]);

  const handleLoadColors = useCallback(async () => {
    try {
      const colorsList = await ColorsService.listColors(user.empresa_id);

      handleChangeColorsOptions(
        colorsList.map((color) => ({
          value: color.id,
          label: color.name,
        })),
      );
    } catch (error) {
      if (error instanceof APIError) {
        addToast({
          title: error.message,
          type: 'error',
        });
      }
    }
  }, [user.empresa_id, addToast, handleChangeColorsOptions]);

  useEffect(() => {
    handleLoadSizes();
    handleLoadColors();
  }, [isDrawerOpened, handleLoadSizes, handleLoadColors]);

  useEffect(() => {
    const fetchBestShipping = async (): Promise<void> => {
      const res = await empresaApi.getParametro(
        user.empresa_id,
        'best_shipping_services',
      );
      if (res) {
        setBestShippingIsActive(JSON.parse(res.role).active);
        setBestShippingIsActive(JSON.parse(res.role).active);
      }

      if (res) {
        if (typeof res.role === 'string') {
          setBestShippingIsActive(JSON.parse(res.role).active);
        } else {
          setBestShippingIsActive(res.role.active);
        }
      }
    };

    fetchBestShipping();
  }, [user.empresa_id]);

  useEffect(() => {
    const defaultGroup = groupsOptions.find((group) => group.value === groupId);
    const defaultUnitOfMeasurement = unitsOfMeasurementOptions.find(
      (unitOfMeasurement) => unitOfMeasurement.value === unitOfMeasurementId,
    );

    formRef.current?.setData({
      name,
      gtin,
      width,
      height,
      length,
      weight,
      observation,
      group: defaultGroup,
      unitOfMeasurement: defaultUnitOfMeasurement,
    });
  }, [
    name,
    gtin,
    observation,
    formRef,
    groupId,
    groupsOptions,
    unitOfMeasurementId,
    unitsOfMeasurementOptions,
    width,
    height,
    length,
    weight,
  ]);

  useEffect(() => {
    if (isFirstRender.current && isDrawerOpened && grids.length) {
      grids
        .sort((a, b) => a.order - b.order)
        .forEach((grid, gridIndex) => {
          if (!formRef.current?.getFieldValue(`grids[${gridIndex}].name`)) {
            formRef.current?.setFieldValue(
              `grids[${gridIndex}].name`,
              grid.name,
            );
          }

          if (!formRef.current?.getFieldValue(`grids[${gridIndex}].price`)) {
            formRef.current?.setFieldValue(
              `grids[${gridIndex}].price`,
              grid.price,
            );
          }

          if (!formRef.current?.getFieldValue(`grids[${gridIndex}].size`)) {
            formRef.current?.setFieldValue(
              `grids[${gridIndex}].size`,
              grid.size,
            );
          }

          if (!formRef.current?.getFieldValue(`grids[${gridIndex}].color`)) {
            formRef.current?.setFieldValue(
              `grids[${gridIndex}].color`,
              grid.color,
            );
          }
          if (!formRef.current?.getFieldValue(`grids[${gridIndex}].discount`)) {
            formRef.current?.setFieldValue(
              `grids[${gridIndex}].discount`,
              grid.discount,
            );
          }

          if (
            !formRef.current?.getFieldValue(`grids[${gridIndex}].minimumStock`)
          ) {
            formRef.current?.setFieldValue(
              `grids[${gridIndex}].minimumStock`,
              grid.minimumStock,
            );
          }

          if (
            !formRef.current?.getFieldValue(`grids[${gridIndex}].currentStock`)
          ) {
            formRef.current?.setFieldValue(
              `grids[${gridIndex}].currentStock`,
              grid.currentStock,
            );
          }

          if (!formRef.current?.getFieldValue(`grids[${gridIndex}].code`)) {
            formRef.current?.setFieldValue(
              `grids[${gridIndex}].code`,
              grid.code,
            );
          }
        });
    }
  }, [grids, isDrawerOpened]);

  useEffect(() => {
    if (!isDrawerOpened) {
      isFirstRender.current = true;

      if (formMainRef.current) {
        formMainRef.current.scrollTop = 0;
      }
    }
  }, [isDrawerOpened]);

  const handleSubmit = useCallback(
    async (data) => {
      if (isComplementsDrawerOpened) return;

      setIsLoading(true);

      formRef.current?.setErrors({});

      try {
        const currentTabSchema = SCHEMAS[currentTab];

        const formValues: any = await currentTabSchema.validate(data, {
          abortEarly: false,
        });

        if (currentTab === LAST_TAB_INDEX || id) {
          await Promise.all(
            Object.values(SCHEMAS).map(async (schema) => {
              await schema.validate(data, {
                abortEarly: false,
              });
            }),
          );

          if (grids.length === 0) {
            handleChangeTab(1);

            addToast({
              title: 'Você deve criar ao menos uma grade',
              type: 'error',
            });

            return;
          }

          const invalidShiftExists = details.shifts.find(
            (shift) => shift.startTime > shift.endTime,
          );

          if (!isAwaysAvailability && invalidShiftExists) {
            handleChangeTab(4);

            addToast({
              title: 'Turno inválido',
              description: 'O início do turno deve ser maior do que o fim',
              type: 'error',
            });

            return;
          }

          await Promise.all(
            ((formValues as any)?.grids as Array<any>).map(
              async (grid: any, index) => {
                if (grid.code) {
                  const data2 = {
                    code: grid.code,
                  };

                  if (typeof grids[index].id === 'number') {
                    Object.assign(data2, {
                      productGridId: grids[index].id,
                    });
                  }

                  await ProductsService.validatePDVCode(
                    user.empresa_id,
                    data2,
                  ).catch((err) => {
                    throw new APIError(
                      `Código já em uso-${`grids[${index}].code`}-${
                        err.message
                      }`,
                    );
                  });
                }
              },
            ),
          );

          const formData = new FormData();

          if (image instanceof File) {
            formData.append('image', image.name);
            formData.append('files', image);
          }

          const parsedComplements = (complements || []).map((complement) => ({
            ...complement,
            id: typeof complement.id === 'number' ? complement.id : undefined,
            items: (complement.items || []).map((item) => {
              const commomItem = {
                ...item,
                id: typeof item.id === 'number' ? item.id : undefined,
                image: undefined,
                price: {
                  value: item.price,
                },
                rawMaterialId:
                  typeof item.rawMaterialId === 'number'
                    ? item.rawMaterialId || undefined
                    : undefined,
              };

              if (item?.image instanceof File) {
                formData.append('files', item.image);

                return {
                  ...commomItem,
                  image: item.image.name,
                };
              }

              return commomItem;
            }),
          }));

          const parsedGrids = grids
            .sort((a, b) => a.order - b.order)
            .map((grid, gridIndex) => {
              const imagesToUpload: Image[] = [];

              grid.images.forEach((gridImage) => {
                if (gridImage) {
                  if (gridImage.file instanceof File) {
                    formData.append('files', gridImage.file);

                    imagesToUpload.push({
                      ...gridImage,
                      file: gridImage.file.name,
                    });
                  } else {
                    imagesToUpload.push(gridImage);
                  }
                }
              });

              return {
                ...grid,
                ...data.grids[gridIndex],
                images: imagesToUpload,
                sizeId: data.grids[gridIndex].size,
                colorId: data.grids[gridIndex].color,
                minimumStock: Number(data.grids[gridIndex].minimumStock || 0),
                currentStock:
                  Number(data.grids[gridIndex].currentStock || 0) +
                  Number(data.grids[gridIndex].addStock || 0),
                id: typeof grid.id === 'number' ? grid.id : undefined,
                price: {
                  value: Number(data.grids[gridIndex].price || 0),
                },
                discount: {
                  value: Number(data.grids[gridIndex].discount || 0) / 100,
                  price:
                    Number(data.grids[gridIndex].price || 0) *
                    (Number(data.grids[gridIndex].discount || 0) / 100),
                  active: Number(data.grids[gridIndex].discount || 0) / 100 > 0,
                },
              };
            });

          const parsedDetails = {
            dias: {
              sexta: isAwaysAvailability || details.days.friday,
              terca: isAwaysAvailability || details.days.tuesday,
              quarta: isAwaysAvailability || details.days.wednesday,
              quinta: isAwaysAvailability || details.days.thursday,
              sabado: isAwaysAvailability || details.days.saturday,
              domingo: isAwaysAvailability || details.days.sunday,
              segunda: isAwaysAvailability || details.days.monday,
            },
            turnos: isAwaysAvailability
              ? []
              : details.shifts.map((shift) => ({
                  inicio: shift.startTime,
                  fim: shift.endTime,
                })),
          };

          if (bestShippingIsActive) {
            if (!data.height) {
              const validationError = new ValidationError(
                ['Campo obrigatório'],
                formValues.height,
                'height',
              );

              validationError.inner = [
                new ValidationError(
                  ['Campo obrigatório'],
                  formValues.height,
                  'height',
                  'min',
                ),
              ];

              throw validationError;
            }
            if (!data.width) {
              const validationError = new ValidationError(
                ['Campo obrigatório'],
                formValues.width,
                'width',
              );

              validationError.inner = [
                new ValidationError(
                  ['Campo obrigatório'],
                  formValues.width,
                  'width',
                  'min',
                ),
              ];

              throw validationError;
            }
            if (!data.length) {
              const validationError = new ValidationError(
                ['Campo obrigatório'],
                formValues.length,
                'length',
              );

              validationError.inner = [
                new ValidationError(
                  ['Campo obrigatório'],
                  formValues.length,
                  'length',
                  'min',
                ),
              ];

              throw validationError;
            }
            if (!data.weight) {
              const validationError = new ValidationError(
                ['Campo obrigatório'],
                formValues.weight,
                'weight',
              );

              validationError.inner = [
                new ValidationError(
                  ['Campo obrigatório'],
                  formValues.weight,
                  'weight',
                  'min',
                ),
              ];

              throw validationError;
            }
          }

          if (data.height) {
            formData.append('height', data.height);
          }

          if (data.width) {
            formData.append('width', data.width);
          }

          if (data.length) {
            formData.append('length', data.length);
          }

          if (data.weight) {
            formData.append('weight', data.weight);
          }

          formData.append('type', '1');
          formData.append('controlGrid', '1');
          formData.append('name', data.name);
          formData.append('groupId', data.group);
          formData.append('gtin', data.gtin);
          formData.append('observation', data.observation);
          formData.append('availability', JSON.stringify(parsedDetails));
          formData.append('complements', JSON.stringify(parsedComplements));
          formData.append('unitOfMeasurementId', data.unitOfMeasurement);
          formData.append('grids', JSON.stringify(parsedGrids));

          let product;

          if (id) {
            product = await ProductsService.editProduct(
              id,
              user.empresa_id,
              data.group,
              formData,
            );
          } else {
            formData.append('order', String(lastProductIndex + 1));
            product = await ProductsService.createProduct(
              user.empresa_id,
              data.group,
              formData,
            );
          }

          let unitOfMeasurementAcronym = '';

          const unitOfMeasurement =
            unitsOfMeasurementOptions.find(
              (foundUnitOfMeasurement) =>
                foundUnitOfMeasurement.value === data.unitOfMeasurement,
            )?.label || '';

          const unitsOfMeasurementMatches = unitOfMeasurement.match(
            /(\(.*?\))|<\w+(\s+("[^"]*"|'[^']*'|[^>])+)?>|<\/\w+>/gi,
          );

          if (unitsOfMeasurementMatches && unitsOfMeasurementMatches.length) {
            unitOfMeasurementAcronym = unitsOfMeasurementMatches[0]
              .replace('(', '')
              .replace(')', '');
          }

          onSuccessSave(data.group, {
            id: product.id,
            name: product.name,
            stock: undefined,
            order: Number(product.order),
            code: product.code,
            unitOfMeasurement: {
              acronym: unitOfMeasurementAcronym,
            },
            image: product.image,
            price: undefined,
            pauseSale: Boolean(product.pauseSale),
            numOfGrids: product?.numOfGrids || 0,
            minGridsPrice: product?.minGridsPrice || 0,
            grids: product?.grids || [],
          });

          handleCancel();

          return;
        }

        handleChangeTab(currentTab + 1);
      } catch (error) {
        console.log(error);

        let errors = {};

        if (error instanceof ValidationError) {
          errors = getValidationErrors(error);

          const detailsTabFields = [
            'group',
            'name',
            'gtin',
            'observation',
            'unitOfMeasurement',
          ];
          const dimensionsTabFields = ['height', 'width', 'length', 'weight'];

          if (
            Object.keys(errors).some((field) =>
              detailsTabFields.includes(field),
            )
          ) {
            handleChangeTab(0);
          }

          if (Object.keys(errors).some((field) => !!field.match(/grids/))) {
            handleChangeTab(1);
          }

          if (
            Object.keys(errors).some((field) =>
              dimensionsTabFields.includes(field),
            )
          ) {
            handleChangeTab(2);
          }

          formRef.current?.setErrors(errors);
        }

        if (error instanceof APIError) {
          if (error.message.includes('Código já em uso')) {
            handleChangeTab(1);

            formRef.current?.setFieldError(
              error.message.split('-')[1],
              'Código já em uso!',
            );
            addToast({
              title: error.message.split('-')[2],
              type: 'error',
            });
          } else {
            addToast({
              title: error.message,
              type: 'error',
            });
          }
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      id,
      image,
      grids,
      details,
      currentTab,
      complements,
      isAwaysAvailability,
      isComplementsDrawerOpened,
      unitsOfMeasurementOptions,
      user.empresa_id,
      lastProductIndex,
      addToast,
      handleCancel,
      onSuccessSave,
      handleChangeTab,
      bestShippingIsActive,
    ],
  );

  return (
    <Container isVisible={isDrawerOpened}>
      <Form ref={formRef} onSubmit={handleSubmit}>
        <header>
          <div className="drawer-title">
            <h1>{id ? 'Editar' : 'Novo'} produto</h1>
            <button
              type="button"
              onClick={handleTryCancel}
              disabled={isLoading}
            >
              <BiX />
            </button>
          </div>
          <TabBar
            selectedTab={currentTab}
            onChange={(_event, value) => handleChangeTab(value)}
          >
            <Tab wrapped style={{ fontSize: '14px' }}>
              Detalhes
            </Tab>
            <Tab wrapped style={{ fontSize: '14px' }}>
              Grades
            </Tab>
            <Tab wrapped style={{ fontSize: '14px' }}>
              Dimensões
            </Tab>
            <Tab wrapped style={{ fontSize: '14px' }}>
              Complementos
            </Tab>
            <Tab wrapped style={{ fontSize: '14px' }}>
              Disponibilidade
            </Tab>
          </TabBar>
        </header>
        <main ref={formMainRef}>
          <TabPanel index={0} selectedTab={currentTab}>
            <Details />
          </TabPanel>
          <TabPanel index={1} selectedTab={currentTab}>
            <Grids formRef={formRef} />
          </TabPanel>
          <TabPanel index={2} selectedTab={currentTab}>
            <Dimensions />
          </TabPanel>
          <TabPanel index={3} selectedTab={currentTab}>
            <Complements />
          </TabPanel>
          <TabPanel index={4} selectedTab={currentTab}>
            <Availability />
          </TabPanel>
        </main>
        <footer>
          <Button
            type="button"
            variant="outlined"
            disabled={isLoading}
            onClick={handleTryCancel}
          >
            Cancelar
          </Button>
          <Button type="submit" loading={isLoading}>
            {currentTab === LAST_TAB_INDEX || id ? 'Salvar' : 'Continuar'}
          </Button>
        </footer>
      </Form>
      <Modal ref={modalRef} containerStyle={{ zIndex: 99999999 }}>
        <ModalHeader>
          <div>
            <IoIosWarning />
          </div>
          <h1>Atenção</h1>
          <button type="button" onClick={() => modalRef.current?.close()}>
            <BiX />
          </button>
        </ModalHeader>
        <p>
          Você não salvou suas alterações. Ao realizar a ação todos os dados
          serão perdidos.
          <br />
          <strong>Deseja prosseguir?</strong>
        </p>
        <Button full onClick={handleCancel}>
          Confirmar
        </Button>
        <Button
          full
          variant="outlined"
          onClick={() => modalRef.current?.close()}
        >
          Cancelar
        </Button>
      </Modal>
    </Container>
  );
};

export default GridProduct;
